import BlitlineService from 'simple_blitline_node'
import UUID from 'uuid/v4'
import MetadataStandards from './metadata_standards'

const BLITLINE_VERSION = 1.21
const BLITLINE_APP_ID  = process.env.BLITLINE_APP_ID
const AWS_BUCKET_NAME  = process.env.AWS_BUCKET_NAME
const WEB_VIEWABLE     = ['png', 'jpg', 'gif']
const NO_CMYK_SUPPORT  = ['png', 'gif']
const FILETYPE_ALIASES = { jpeg: 'jpg', tif: 'tiff', postscript: 'ai', 'svg+xml': 'svg' }

// http://helpdocs.blitline.com/article/Mk6sdI16XI-formats
const SUPPORTED_FILETYPES = [
  'png', 'jpg', 'tiff', 'bmp', 'pdf', 'psd',
  'gif', 'webp', 'cr2', 'dcm', 'dicom', 'ai',
  'svg', 'eps', 'arw', 'nef', 'orf', 'rw2'
]

const CONVERSION_MAPPINGS = {
  png: ['png', 'jpg', 'gif', 'svg'],
  jpg: ['jpg', 'png', 'gif', 'svg'],
  tiff: ['tiff', 'png', 'jpg'],
  bmp: ['bmp', 'png', 'jpg'],
  pdf: ['pdf', 'png', 'jpg'],
  psd: ['psd', 'png', 'jpg'],
  webp: ['webp', 'png', 'jpg'],
  cr2: ['cr2', 'png', 'jpg'],
  dcm: ['dcm', 'png', 'jpg'],
  dicom: ['dicom', 'png', 'jpg'],
  ai: ['ai', 'png', 'jpg'],
  svg: ['svg', 'png', 'jpg'],
  eps: ['eps', 'png', 'jpg'],
  gif: ['gif', 'png', 'jpg'],
  arw: ['arw', 'png', 'jpg'],
  nef: ['nef', 'png', 'jpg'],
  orf: ['orf', 'png', 'jpg'],
  rw2: ['rw2', 'png', 'jpg']
}

export default class Blitline {
  constructor(url, filetype) {
    this.client          = new BlitlineService()
    this.url             = url
    this.filetype        = Blitline.normalizeFiletype(filetype)
    this.extractMetadata = this.extractMetadata.bind(this)
    this.extractColors   = this.extractColors.bind(this)
    this.extractTags     = this.extractTags.bind(this)
    this.extractText     = this.extractText.bind(this)
    this.thumbnail       = this.thumbnail.bind(this)
    this.convertTo       = this.convertTo.bind(this)
  }

  static hasSupportFor(filetype) {
    filetype = this.normalizeFiletype(filetype)
    return SUPPORTED_FILETYPES.includes(filetype)
  }

  static conversionOptionsFor(filetype) {
    filetype = this.normalizeFiletype(filetype)
    return CONVERSION_MAPPINGS[filetype]
  }

  static webViewable(filetype) {
    filetype = this.normalizeFiletype(filetype)
    return WEB_VIEWABLE.includes(filetype)
  }

  static cmykCompatible(filetype) {
    filetype = this.normalizeFiletype(filetype)
    return !NO_CMYK_SUPPORT.includes(filetype)
  }

  static normalizeFiletype(filetype) {
    filetype = filetype.toLowerCase()
    return FILETYPE_ALIASES[filetype] || filetype
  }

  extractMetadata() {
    this.client.addJob({
      application_id: BLITLINE_APP_ID,
      v: BLITLINE_VERSION,
      src: this.url,
      pre_process: { peek: true }
    })

    return this.client.postJobs().then(data => {
      let job_id = data.results[0].job_id
      return this.pollForJobResults(job_id)
    }).then(response => {
      return response.results.original_meta.original_exif
    }).catch(error => {
      return null
    })
  }

  extractColors() {
    this.client.addJob({
      application_id: BLITLINE_APP_ID,
      v: BLITLINE_VERSION,
      src: this.url,
      extract_colors: { max_colors: 6 },
      functions: [{ name: 'no_op', params: {} }]
    })
    return this.client.postJobs().then(data => {
      let job_id = data.results[0].job_id
      return this.pollForJobResults(job_id)
    }).then(response => {
      let uniq_colors = []
      let colors = response.results.original_meta.extracted_colors.data
      colors = colors.filter(color => {
        let { hex_color } = color.color
        let should_filter = !uniq_colors.includes(hex_color)
        uniq_colors.push(hex_color)
        return should_filter
      })
      return colors
    }).catch(error => {
      return null
    })
  }

  extractTags() {
    this.client.addJob({
      application_id: BLITLINE_APP_ID,
      v: BLITLINE_VERSION,
      src: this.url,
      detect_labels: true,
      functions: [{ name: 'no_op', params: {} }]
    })

    return this.client.postJobs().then(data => {
      let job_id = data.results[0].job_id
      return this.pollForJobResults(job_id)
    }).then(response => {
      let confidence_threshold = process.env.AUTO_TAG_CONFIDENCE_THRESHOLD
      let tags = response.results.original_meta.detect_labels.labels
      tags     = tags.filter(tag => { return tag.confidence > confidence_threshold })
      return tags.map(tag => { return tag.name })
    }).catch(error => {
      return null
    })
  }

  extractText() {
    this.client.addJob({
      application_id: BLITLINE_APP_ID,
      v: BLITLINE_VERSION,
      src: this.url,
      get_tika: true,
      functions: [{ name: 'no_op', params: {} }]
    })

    return this.client.postJobs().then(data => {
      let job_id = data.results[0].job_id
      return this.pollForJobResults(job_id)
    }).then(response => {
      try {
        return response.results.original_meta.tika_results[0].text
      } catch (error) {
        return null
      }
    }).catch(error => {
      return null
    })
  }

  thumbnail() {
    let job_id
    let key = `${UUID()}-thumbnail`
    let function_name = this.mimetype == 'image/gif' ? 'resize_gif_to_fit' : 'resize_to_fit'
    this.client.addJob({
      application_id: BLITLINE_APP_ID,
      v: BLITLINE_VERSION,
      src: this.url,
      src_type: 'smart_image',
      src_data: { colorspace: 'srgb' },
      functions: [{
        name: function_name,
        params: {
          width: 1000,
          height: 1000,
          only_shrink_larger: true
        },
        save: {
          image_identifier: key,
          quality: 100,
          image_optim: true,
          s3_destination: {
            bucket: AWS_BUCKET_NAME,
            key: `workbench/${key}`
          }
        }
      }]
    })
    return this.client.postJobs().then(data => {
      job_id = data.results[0].job_id
      return this.pollForJobResults(job_id)
    }).then(response => {
      if (response.results.images[0].s3_url) {
        // default s3_url is not https and cannot be used in AJAX download.
        // return https://s3.amazonaws.com/blahblahblah instead
        return `https://s3.amazonaws.com/${AWS_BUCKET_NAME}/workbench/${key}`
      } else {
        throw new Error(`Workbench thumbnail failed. Job ID: ${job_id}`)
      }
    }).catch(error => {
      return null
    })
  }

  convertTo(filetype, colorspace=null, width=null, height=null, metadata=null) {
    filetype = Blitline.normalizeFiletype(filetype)
    let job_id
    let needs_extra_colorspace_conversion = false
    let key = UUID()
    let blitline_job = {
      application_id: BLITLINE_APP_ID,
      v: BLITLINE_VERSION,
      src: this.url,
      src_data: {},
      functions: []
    }

    let blitline_function = {
      name: 'no_op',
      params: {},
      save: {
        image_identifier: key,
        extension: `.${filetype}`,
        save_metadata: true,
        quality: 100,
        s3_destination: {
          bucket: AWS_BUCKET_NAME,
          key: `workbench/${key}`
        }
      }
    }

    if (!!metadata && Object.keys(metadata).length > 0) {
      let filtered_metadata   = {}
      let standard_properties = MetadataStandards.iptc_property_names()
      let filtered_properties = Object.keys(metadata).filter(property => {
        return standard_properties.includes(property.toLowerCase())
      })

      filtered_properties.map(property => {
        filtered_metadata[property] = metadata[property]
      })

      if (Object.keys(filtered_metadata).length > 0) {
        blitline_function.save.set_exif = filtered_metadata
      }
    }

    if (!!width && !!height) {
      blitline_function.name = this.filetype == 'gif' ? 'resize_gif_to_fit' : 'resize_to_fit'
      Object.assign(blitline_function.params, { width, height })
    }

    if (!!colorspace) {
      if (colorspace == 'cmyk' && !Blitline.cmykCompatible(this.filetype) && Blitline.cmykCompatible(filetype)) {
        needs_extra_colorspace_conversion = true
      }
      Object.assign(blitline_job.src_data, { colorspace })
    }

    blitline_job.functions.push(blitline_function)
    this.client.addJob(blitline_job)

    return this.client.postJobs().then(data => {
      job_id = data.results[0].job_id
      return this.pollForJobResults(job_id)
    }).then(response => {
      if (!response.results.error) {
        // default s3_url is not https and cannot be used in AJAX download.
        // return https://s3.amazonaws.com/blahblahblah instead
        let converted_url = `https://s3.amazonaws.com/${AWS_BUCKET_NAME}/workbench/${key}`
        if (!!needs_extra_colorspace_conversion) {
          this.url      = converted_url
          this.filetype = filetype
          return this.convertTo(filetype, colorspace, null, null, metadata)
        } else {
          return converted_url
        }
      } else {
        throw new Error(`Workbench conversion failed. Job ID: ${job_id}`)
      }
    }).catch(error => {
      return null
    })
  }

  pollForJobResults(job_id) {
    return $.ajax({
      type: 'GET',
      url: `https://cache.blitline.com/listen/${job_id}`
    }).then(data => {
      return JSON.parse(data)
    }).catch(error => {
      return null
    })
  }
}
